import * as React from 'react'
import { FC, useCallback } from 'react'
import { Theme } from '@mui/material/styles'

import { Form, useFormikContext } from 'formik'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import { LeadStatusSelect } from '@user/components/Leads/AddLeadPage/FormFields/LeadStatusSelect/LeadStatusSelect'
import { DensityType } from '@user/components/Opportunities/AddOpportunityPage/typings'
import { useTidalThemeActive } from '@user/features/TidalThemeToggle'
import { IReOpenFormModel } from '@user/components/Leads/ReOpenDialog/ReOpenLeadDialog'
import Typography from '@mui/material/Typography'
import { ISalesFunnelStage } from '@user/store/salesFunnel'
import { OwnerSelect } from '@user/components/Leads/ReOpenDialog/OwnerSelect'

const styles = {
  input: {
    display: 'flex',
    marginTop: (theme: Theme) => theme.spacing(1),
  },

  submitBtn: {
    marginRight: (theme: Theme) => theme.spacing(1),
  },
}

export interface SalesTeamUser {
  id: string | null
  fullName: string
  isPosition?: boolean
  level?: number
}

type Props = {
  onClose: () => void
  salesTeamId: string
  salesFunnelStages: ISalesFunnelStage[]
  owners: SalesTeamUser[]
}

export const ReOpenLeadFormNew: FC<Props> = (props: Props) => {
  const { onClose, salesFunnelStages, owners } = props
  const tidalThemeActive = useTidalThemeActive()

  const { isSubmitting, isValid, submitForm, setFormikState } =
    useFormikContext<IReOpenFormModel>()
  const density: DensityType = tidalThemeActive ? 'normal' : 'compact'

  const onSubmitForm = useCallback(() => {
    if (isValid) {
      submitForm()
    } else {
      setFormikState((prevState) => ({
        ...prevState,
        submitCount: prevState.submitCount + 1,
        touched: {
          ...prevState.touched,
          salesFunnelStageId: true,
          owner: true,
        },
      }))
    }
  }, [isValid, setFormikState, submitForm])

  return (
    <Form autoComplete="off" style={{ padding: '0px 4px 4px' }}>
      <Typography variant="body1" mb={1}>
        Select a new Lead Status
      </Typography>

      <Box sx={{ maxWidth: '284px' }}>
        <LeadStatusSelect
          name="salesFunnelStageId"
          label="Lead Status"
          disabled={isSubmitting}
          salesFunnelStages={salesFunnelStages}
          density={density}
          required
        />
      </Box>

      <Typography variant="body1" mb={1} mt={2}>
        Select a new Owner
      </Typography>
      <Box sx={{ maxWidth: '284px' }}>
        <OwnerSelect owners={owners} />
      </Box>

      <Box mt={3} display="flex" justifyContent="flex-end">
        <Button
          onClick={onClose}
          variant="text"
          sx={styles.submitBtn}
          data-test-id="reOpenLeadCancelBtn"
        >
          Cancel
        </Button>

        <Button
          color="primary"
          data-test-id="reOpenLeadSaveBtn"
          disabled={isSubmitting}
          variant="contained"
          onClick={onSubmitForm}
        >
          Save
        </Button>
      </Box>
    </Form>
  )
}

ReOpenLeadFormNew.displayName = 'ReOpenLeadForm'
